// src/features/players/components/PlayerDetailPage.tsx

import { useParams } from '@tanstack/react-router';
import { Stack, Grid, Loader, Alert, Text, Tabs, Group, Anchor, Badge, Tooltip } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconFileDescription,
  IconSettings,
  IconAd2,
  IconShield,
  IconPalette,
} from '@tabler/icons-react';
import { bcLong } from '@lib2/model/util/string/BaseConverter';
import { formatTimestamp } from '@/shared/utils/date';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { usePlayer } from '../usePlayer';
import { OverviewTab } from './OverviewTab';
import { SettingsTab } from './SettingsTab';
import { AdvertisingTab } from './AdvertisingTab';
import { SecurityTab } from './SecurityTab';
import { AppearanceTab } from './AppearanceTab';

export function PlayerDetailPage() {
  const { id: displayId } = useParams({ strict: false }) as { id: string };
  const playerId = bcLong.getNum(displayId);
  const { selectedCustomer } = useAuth();

  const { player, isLoading, isError, error } = usePlayer(playerId);

  if (isLoading) {
    return (
      <Stack align="center" justify="center" h={400}>
        <Loader size="lg" />
        <Text c="dimmed">Loading player...</Text>
      </Stack>
    );
  }

  if (isError || !player) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
        {error?.message ?? 'Failed to load player'}
      </Alert>
    );
  }

  const { core } = player;
  const allowAds = selectedCustomer?.cfg.quota.features.account.allowAds === true;

  return (
    <Stack gap="lg">
      {/* Header */}
      <Stack gap="xs">
        <Anchor component={Link} to="/players" size="sm" c="dimmed">
          <Group gap={4}>
            <IconArrowLeft size={14} />
            Back to Players
          </Group>
        </Anchor>

        <Stack gap={4}>
          <Text fw={700} size="xl">{core.label}</Text>
          <Group gap="sm">
            <Text size="sm" c="dimmed">ID: {displayId}</Text>
            <Text size="sm" c="dimmed">·</Text>
            <Badge color={core.enabled ? 'green' : 'gray'} variant="light" size="sm">
              {core.enabled ? 'Active' : 'Inactive'}
            </Badge>
            <Text size="sm" c="dimmed">·</Text>
            <Tooltip label={formatTimestamp(core.created)} openDelay={400}>
              <Text size="sm" c="dimmed">Created {formatTimestamp(core.created)}</Text>
            </Tooltip>
          </Group>
        </Stack>
      </Stack>

      {/* Tabs */}
      <Tabs defaultValue="overview" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconFileDescription size={16} />}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab value="settings" leftSection={<IconSettings size={16} />}>
            Settings
          </Tabs.Tab>
          {allowAds && (
            <Tabs.Tab value="advertising" leftSection={<IconAd2 size={16} />}>
              Advertising
            </Tabs.Tab>
          )}
          <Tabs.Tab value="security" leftSection={<IconShield size={16} />}>
            Security & Access
          </Tabs.Tab>
          <Tabs.Tab value="appearance" leftSection={<IconPalette size={16} />}>
            Appearance
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview" pt="md" keepMounted>
          <OverviewTab player={player} />
        </Tabs.Panel>

        <Tabs.Panel value="settings" pt="md" keepMounted>
          <SettingsTab player={player} />
        </Tabs.Panel>

        {allowAds && (
          <Tabs.Panel value="advertising" pt="md" keepMounted>
            <AdvertisingTab player={player} />
          </Tabs.Panel>
        )}

        <Tabs.Panel value="security" pt="md" keepMounted>
          <SecurityTab player={player} />
        </Tabs.Panel>

        <Tabs.Panel value="appearance" pt="md" keepMounted>
          <AppearanceTab player={player} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
